import {useRef} from 'react';
import {Body2} from '@ui/atoms/typography';
import {TextField} from '@ui/molecules/field/textfield';
import {ToggleSwitch} from '@ui/atoms/toggle-switch';
import {MinusIcon} from '@ui/atoms/icons';
import {QuestionData} from './types';
import {
  AdditionalHint,
  BlockContent,
  BlockDragRail,
  DragIcon,
  QuestionBankAction,
  QuestionBankButton,
  QuestionBankPlus,
  QuestionBlockHeader,
  QuestionBody,
  QuestionCard,
  QuestionFooter,
  QuestionSourceLabel,
  QuestionTypePill,
  ReadOnlyQuestion,
  RemoveIcon,
  RemoveRow,
  ToggleRow,
} from './content-step.styles';

export interface SingleQuestionBlockProps {
  compact?: boolean;
  data: QuestionData;
  onChange: (data: QuestionData) => void;
  onRemove: () => void;
  dragHandleProps: any;
  showValidation?: boolean;
}

export const SingleQuestionBlock = ({
  compact = false,
  data,
  onChange,
  onRemove,
  dragHandleProps,
  showValidation = false,
}: SingleQuestionBlockProps) => {
  const requiredSwitchId = useRef(Math.random() * 10);
  const source =
    data.source || (data.type === 'custom' ? 'custom' : 'template');
  const questionError = showValidation && !data.text?.trim();
  const canSaveToBank = (data.text?.trim().length ?? 0) > 3;

  return (
    <QuestionCard data-block-type="question">
      <BlockDragRail {...dragHandleProps} aria-label="Reorder question">
        <DragIcon aria-hidden="true">⠿</DragIcon>
      </BlockDragRail>
      <BlockContent spacing="default">
        <QuestionBlockHeader>
          <QuestionTypePill variant="question">Question</QuestionTypePill>
          <RemoveRow onClick={onRemove} aria-label="Remove question">
            <RemoveIcon>
              <MinusIcon />
            </RemoveIcon>{' '}
            Remove
          </RemoveRow>
        </QuestionBlockHeader>

        <QuestionBody>
          {source === 'template' ? (
            <>
              <QuestionSourceLabel>
                Question library | {data.templateCategory || 'My questions'}
              </QuestionSourceLabel>
              <ReadOnlyQuestion
                role="textbox"
                aria-readonly="true"
                aria-label="Question from library">
                {data.text}
              </ReadOnlyQuestion>
            </>
          ) : (
            <TextField
              placeholder="What's the update with Project Beam?"
              autoComplete="off"
              inputStyle={{
                borderRadius: '10px',
                ...(compact
                  ? {
                      height: '44px',
                      padding: '5px 12px',
                      fontSize: '14px',
                      lineHeight: '22px',
                    }
                  : {}),
              }}
              label="Enter question"
              type="text"
              value={data.text || ''}
              onChange={(event) =>
                onChange({
                  ...data,
                  type: 'custom',
                  source: 'custom',
                  answerType: data.answerType || 'text',
                  text: event.target.value,
                  saveToQuestionBank:
                    event.target.value.trim().length > 3
                      ? data.saveToQuestionBank
                      : false,
                })
              }
              name="questionText"
              state={questionError ? 'error' : 'default'}
              helper={questionError ? 'Enter a question' : undefined}
              margin
            />
          )}

          <AdditionalHint>
            Answer type is text (paragraph) by default
          </AdditionalHint>

          <QuestionFooter>
            <ToggleRow>
              <ToggleSwitch
                togglWidth="sm"
                checked={!data.optional}
                switchId={requiredSwitchId.current}
                onChange={(event) =>
                  onChange({...data, optional: !event.target.checked})
                }
              />
              <Body2 kind="blackTwo" weight="semibold">
                Required
              </Body2>
            </ToggleRow>

            {source === 'custom' && (
              <QuestionBankAction>
                {!data.saveToQuestionBank && (
                  <QuestionBankPlus
                    aria-hidden="true"
                    disabled={!canSaveToBank}>
                    +
                  </QuestionBankPlus>
                )}
                <QuestionBankButton
                  type="button"
                  saved={data.saveToQuestionBank}
                  disabled={!canSaveToBank}
                  aria-pressed={data.saveToQuestionBank || false}
                  onClick={() =>
                    onChange({
                      ...data,
                      saveToQuestionBank: !data.saveToQuestionBank,
                    })
                  }>
                  {data.saveToQuestionBank
                    ? 'Saved to question library'
                    : 'Save to question library'}
                </QuestionBankButton>
              </QuestionBankAction>
            )}
          </QuestionFooter>
        </QuestionBody>
      </BlockContent>
    </QuestionCard>
  );
};
